import qs from "qs";

import {
  AuthenticateMethod,
  type AuthenticateResultErrorType,
  type DataTypeStatus,
} from "@medbillai/graphql-types";

import type { CaseType } from "@medbillai/schemas";

export type LoginParams = {
  forceLogout?: boolean;
  authError?: AuthenticateResultErrorType;
  redirect?: string;
};

export type PreviewParams = {
  title: string;
  uri: string;
};

export type HipaaReleaseParams = {
  templateId?: string;
  eventId?: string;
  issueId?: string;
};

export type HipaaViewReleaseParams = {
  templateReleaseId: string;
  templateDisplayName?: string;
};

export type HipaaErrorParams = {
  viewPdfError?: boolean;
};

export interface CreateAuthRedirectUriOptions {
  baseUri: string;
  platform: "ios" | "android" | "web";
  method: AuthenticateMethod;
  applinkSchemeUri?: string;
}

export type FeedbackParams = {
  issueId: string;
  rating?: number;
  feedbackId?: string;
};

export type FeedbackDeepLinkParams = FeedbackParams & {
  showFeedback?: boolean;
};

export type AuthenticateRedirectUrlParams = {
  token: string;
  platform: string;
};

export type StytchAuthenticationParams = AuthenticateRedirectUrlParams & {
  stytch_token_type: string;
};

export type PreviewCaseMessageInfo =
  | {
      type: "USER";
      message: string;
    }
  | {
      type: "SYSTEM";
      messageId: string;
      message: string;
      options?: string[];
    };

export const CASE_PREVIEW_ID = "preview";

/**
 * Appends query parameters to a URL, handling both URLs with and without existing query strings.
 * If the URL already contains query parameters (has a '?'), new params are appended with '&'.
 * Otherwise, new params are appended with '?'.
 */
const buildUrlWithParams = (
  url: string,
  params?: Record<string, unknown>,
): string => {
  const filteredParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value !== undefined),
  );
  if (Object.keys(filteredParams).length > 0) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${qs.stringify(filteredParams)}`;
  }
  return url;
};

// TODO - look into generating these

// Onboarding & auth
export const welcomeUrl = (): string => "/welcome";
export const welcomeInfoUrl = (): string => "/welcome-info";
export const getStartedUrl = (): string => "/get-started";
export const loginUrl = (params?: LoginParams): string => {
  if (!params) {
    return "/login";
  }
  return `/login?${qs.stringify(params)}`;
};
export const verifyEmailUrl = (email: string): string => {
  if (!email) {
    return "/login";
  }
  return `/verify-email?email=${encodeURIComponent(email)}`;
};
// Used for magic links with the paste url button.
export const authenticateUrl = (params: StytchAuthenticationParams): string =>
  buildUrlWithParams("/authenticate", params);

export const createAuthRedirectUri = ({
  baseUri,
  applinkSchemeUri,
  platform,
  method,
}: CreateAuthRedirectUriOptions): string => {
  // In this case we want to ensure the redirect is always navigated back into
  // the app. Google oauth if already authenticated will stay in the iframe, so
  // we use the applinkSchemeUri to navigate back into the app.
  // If the applinkSchemeUri is not set, we default to the baseUri.
  if (
    method === AuthenticateMethod.OAUTH &&
    (platform === "ios" || platform === "android") &&
    applinkSchemeUri
  ) {
    return `${applinkSchemeUri}authenticate?platform=${platform}`;
  }
  const url = new URL("/authenticate", baseUri);
  url.searchParams.set("platform", platform);
  return url.toString();
};

// Web Sign Up Onboarding
export const webSignUpOnboardingUrl = (): string => "/join";
export const webSignUpOnboardingUserNeedsUrl = (): string => "/join/user-needs";
export const webSignUpOnboardingUsageScopeUrl = (): string =>
  "/join/usage-scope";

// Home Tabs
export const homeUrl = (): string => "/";
// Should only be used when navigating inside the tab router
// TODO: Because of the limitation of using headerLarge needing a stack router
// we cannot have a default initial route for the tab router, we need to update
// all routing to use /home but in the mean time because it is the default route
// we can use this till we update all routing, this will effect the web app
// minorly but it is a small fix
export const homeTabsUrl = (): string => "/home";
export const insuranceUrl = (): string => "/insurance";
export const issuesUrl = (): string => `/cases`;
export const billsUrl = (): string => `/bills`;
export const flaggedBillsUrl = (): string => `/bills/flagged`;

// Flow
export const documentUrl = (documentId: string): string =>
  `/document/${documentId}`;
export const documentPreviewUrl = () => "/document/preview";

export const issueUrl = ({
  issueId,
  unread = false,
  title,
}: {
  issueId: string;
  unread?: boolean;
  title?: string | null;
}): string => {
  const baseUrl = `/case/${issueId}`;
  const params = {
    unread: unread.toString(),
    ...(title && { title }),
  };
  return buildUrlWithParams(baseUrl, params);
};
export const issuePreviewUrl = ({
  title,
  caseType,
  storeKey,
}: {
  title?: string;
  caseType: CaseType;
  storeKey?: string;
}) => {
  let url = `/case/${CASE_PREVIEW_ID}?caseType=${caseType}`;
  if (title) {
    url += `&title=${encodeURIComponent(title)}`;
  }
  if (storeKey) {
    url += `&storeKey=${encodeURIComponent(storeKey)}`;
  }
  return url;
};

// Bills
export const billUrl = (
  eobId: string,
  billName: string,
  decision: string,
): string => {
  return `/bill/${eobId}?billName=${billName}&decision=${decision}`;
};
export const billDisputeUrl = (eobId: string): string => {
  return `/bill-dispute?id=${eobId}`;
};

// Configuration / Onboarding
export const linkInsuranceUrl = (): string => "/link-insurance";
export const allowNotificationsUrl = (): string => "/allow-notifications";
export const onboardingCompletedUrl = (): string => "/completed";

// Hipaa Release
export const hipaaReleaseUrl = (params?: HipaaReleaseParams): string =>
  `/hipaa-release?${qs.stringify(params)}`;
export const hipaaViewReleaseUrl = (params?: HipaaViewReleaseParams): string =>
  `/hipaa-release-preview?${qs.stringify(params)}`;
// Deprecated: TODO: Remove this
export const hipaaReleasePreviewUrl = (): string =>
  "/hipaa-release/release-preview";
// Deprecated: TODO: Remove this
export const hipaaReleaseSignatureUrl = (): string =>
  "/hipaa-release/release-signature";
export const hipaaReleaseSuccessUrl = (): string => "/hipaa-release/success";
export const hipaaReleaseErrorUrl = (params?: HipaaErrorParams): string =>
  `/hipaa-release/error?${qs.stringify(params)}`;
export const hipaaReleasePatientSelect = (): string =>
  "/hipaa-release/patient-select";
export const hipaaReleasePatientInformation = (): string =>
  "/hipaa-release/patient-information";
export const hipaaReleasePatientAddress = (): string =>
  "/hipaa-release/patient-address";
export const hipaaReleasePatientContact = (): string =>
  "/hipaa-release/patient-contact";
export const hipaaReleasePatientPhone = (): string =>
  "/hipaa-release/patient-phone";
export const hipaaReleasePatientEmail = (): string =>
  "/hipaa-release/patient-email";
export const hipaaReleasePatientInsurance = (): string =>
  "/hipaa-release/patient-insurance";
export const hipaaReleaseDependentCheck = (): string =>
  "/hipaa-release/dependent-check";
export const hipaaReleaseDependentInformation = (): string =>
  "/hipaa-release/dependent-information";
export const hipaaReleaseSubscriberCheck = (): string =>
  "/hipaa-release/subscriber-check";
export const hipaaReleaseSubscriberInformation = (): string =>
  "/hipaa-release/subscriber-information";
export const hipaaReleasePatientAdditional = (): string =>
  "/hipaa-release/patient-additional";
export const hipaaReleaseSubscriberSelect = (): string =>
  "/hipaa-release/subscriber-select";
export const hipaaReleaseReview = (): string => "/hipaa-release/review";
export const hipaaReleaseSign = (): string => "/hipaa-release/sign";
export const hipaaReleaseSuccess = (): string => "/hipaa-release/success";
export const hipaaReleaseSubscriberInsurance = (): string =>
  "/hipaa-release/subscriber-insurance";
export const hipaaReleaseSubscriberAdditional = (): string =>
  "/hipaa-release/subscriber-additional";

// Onboarding Insurance
export const onboardingInsuranceLinkSelectUrl = (): string =>
  "/onboarding-insurance/select";
export const onboardingInsuranceLinkErrorUrl = (): string =>
  "/onboarding-insurance/error";
export const onboardingInsuranceUnsupportedPlanUrl = (): string =>
  "/onboarding-insurance/unsupported-plan";

export const onboardingFlowInsuranceLinkSelectUrl = (): string =>
  "/onboarding/insurance/select";
export const onboardingFlowInsuranceLinkErrorUrl = (): string =>
  "/onboarding/insurance/error";
export const onboardingFlowInsuranceUnsupportedPlanUrl = (): string =>
  "/onboarding/insurance/unsupported-plan";
export const onboardingFlowInsuranceExitSurveyUrl = (): string =>
  "/onboarding/insurance/link/exit-survey";
export const onboardingFlowInsuranceExitSurveyExtraUrl = (): string =>
  "/onboarding/insurance/link/exit-survey-extra";

// onboardingInsurance Agent
export const onboardingInsuranceWebAgentUrl = (): string =>
  "/onboarding-insurance/link";
export const onboardingInsuranceWebAgentMfaSelectUrl = (): string =>
  "/onboarding-insurance/link/mfa-select";
export const onboardingInsuranceWebAgentMfaCodeUrl = (): string =>
  "/onboarding-insurance/link/mfa-code";
export const onboardingInsuranceWebAgentSecurityQuestionUrl = (): string =>
  "/onboarding-insurance/link/security-question";
export const onboardingInsuranceWebAgentSuccessUrl = (): string =>
  "/onboarding-insurance/link/success";
export const onboardingInsuranceWebAgentAlternateInsuranceUrl = (): string =>
  "/onboarding-insurance/link/alternate-insurance";

export const onboardingFlowInsuranceWebAgentUrl = (): string =>
  "/onboarding/insurance/link";
export const onboardingFlowInsuranceWebAgentMfaSelectUrl = (): string =>
  "/onboarding/insurance/link/mfa-select";
export const onboardingFlowInsuranceWebAgentMfaCodeUrl = (): string =>
  "/onboarding/insurance/link/mfa-code";
export const onboardingFlowInsuranceWebAgentSecurityQuestionUrl = (): string =>
  "/onboarding/insurance/link/security-question";
export const onboardingFlowInsuranceWebAgentSuccessUrl = (): string =>
  "/onboarding/insurance/link/success";
export const onboardingFlowInsuranceWebAgentNotificationsUrl = (): string =>
  "/onboarding/insurance/link/insurance-notifications";
export const onboardingFlowInsuranceWebAgentAlternateInsuranceUrl =
  (): string => "/onboarding/insurance/link/alternate-insurance";

// onboarding Insurance Flexpa
export const onboardingInsuranceFlexpaLinkUrl = (): string =>
  "/onboarding-insurance/connect/login";
export const onboardingInsuranceFlexpaCheckPlanUrl = (): string =>
  "/onboarding-insurance/connect/check-plan";
export const onboardingInsuranceFlexpaSuccessUrl = (): string =>
  "/onboarding-insurance/connect/success";
export const onboardingInsuranceFlexpaAlternateInsuranceUrl = (): string =>
  "/onboarding-insurance/connect/alternate-insurance";

export const onboardingFlowInsuranceFlexpaLinkUrl = (): string =>
  "/onboarding/insurance/connect/login";
export const onboardingFlowInsuranceFlexpaCheckPlanUrl = (): string =>
  "/onboarding/insurance/connect/check-plan";
export const onboardingFlowInsuranceFlexpaSuccessUrl = (): string =>
  "/onboarding/insurance/connect/success";
export const onboardingFlowInsuranceFlexpaNotificationsUrl = (): string =>
  "/onboarding/insurance/connect/insurance-notifications";
export const onboardingFlowInsuranceFlexpaAlternateInsuranceUrl = (): string =>
  "/onboarding/insurance/connect/alternate-insurance";

// Case Intake

// This should most likely never be used directly.
// When navigating to a case intake flow, use the useSetupCaseFlow hook
export const caseIntakeUrl = (): string => {
  return `/case-intake`;
};

export const caseIntakeFlowUrl = (step: string): string => {
  return `${caseIntakeUrl()}/${step}`;
};
export const caseIntakeFlowErrorUrl = (): string => "/case-intake/error";
export const caseDetailsUrl = (caseId: string): string => {
  return `/case/details/${caseId}`;
};
// Settings
export const settingsUrl = (): string => "/settings";
export const settingPreviewUrl = (params: PreviewParams): string => {
  return `/settings/preview?${qs.stringify(params)}`;
};
export const settingsReleaseListUrl = (): string => "/settings/release-list";
export const settingsAccountSettingsUrl = (): string => "/settings/account";
export const settingsDeleteAccountUrl = (): string =>
  "/settings/delete-account";
export const settingsMmfaAccountsUrl = (): string => "/settings/mmfa-accounts";

export const settingsReceiptsUrl = (): string => "/settings/receipts";

// Insurance
export type InsuranceLinkType = "insurance" | "ehr" | "both";
export type InsuranceLinkFrom =
  | {
      placeFrom: "insuranceHome";
      linkType?: InsuranceLinkType;
    }
  | {
      placeFrom: "chat";
      chatId: string;
      linkType?: InsuranceLinkType;
    };

export const insuranceLinkSelectUrl = (from?: InsuranceLinkFrom): string =>
  buildUrlWithParams("/insurance/select", from);
export const insuranceLinkErrorUrl = (): string => "/insurance/error";
export const insuranceLinkUnsupportedPlanUrl = (): string =>
  "/insurance/unsupported-plan";
export const insuranceLinkExitSurveyUrl = (): string =>
  "/insurance/link/exit-survey";
export const insuranceLinkExitSurveyExtraUrl = (): string =>
  "/insurance/link/exit-survey-extra";

export const insuranceNotificationsUrl = (): string =>
  "/insurance/insurance-notifications";
// Insurance Agent
export const insuranceWebAgentLinkUrl = (): string => "/insurance/link";

export type InsuranceWebAgentRelinkUrlParams = {
  mmfaConfirmed?: boolean;
};
export const insuranceWebAgentRelinkUrl = ({
  linkId,
  params,
}: {
  linkId: string;
  params?: InsuranceWebAgentRelinkUrlParams;
}): string => {
  return buildUrlWithParams(`/insurance/link/relink/${linkId}`, params);
};

export const insuranceWebAgentMfaSelectUrl = (): string =>
  "/insurance/link/mfa-select";
export const insuranceWebAgentMfaCodeUrl = (): string =>
  "/insurance/link/mfa-code";
export const insuranceWebAgentSecurityQuestionUrl = (): string =>
  "/insurance/link/security-question";
export const insuranceWebAgentSuccessUrl = (params: {
  hasBenefitsSummary: boolean;
}): string => {
  const baseUrl = "/insurance/link/success";
  return `${baseUrl}?hasBenefitsSummary=${params.hasBenefitsSummary.toString()}`;
};
export const insuranceWebAgentNotificationsUrl = (): string =>
  "/insurance/link/insurance-notifications";
export const insuranceWebAgentAlternateInsuranceUrl = (): string =>
  "/insurance/link/alternate-insurance";
export const insuranceWebAgentManagedMfaApprovalUrl = (): string =>
  "/insurance/link/managed-mfa";
export const insuranceWebAgentPlanDocumentsLoadingUrl = (): string =>
  "/insurance/link/plan-documents-loading";
export function insuranceWebAgentPlanDocumentsLoadedUrl(
  status: DataTypeStatus.SUCCESS,
  caseId: string,
): string;
export function insuranceWebAgentPlanDocumentsLoadedUrl(
  status: Exclude<DataTypeStatus, DataTypeStatus.SUCCESS>,
): string;
export function insuranceWebAgentPlanDocumentsLoadedUrl(
  status: DataTypeStatus,
  caseId?: string,
): string {
  return buildUrlWithParams("/insurance/link/plan-documents-loaded", {
    status,
    caseId,
  });
}

// Insurance Flexpa
export const insuranceFlexpaLinkUrl = (): string => "/insurance/connect/login";
export const insuranceFlexpaCheckPlanUrl = (): string =>
  "/insurance/connect/check-plan";
export const insuranceFlexpaSuccessUrl = (): string =>
  "/insurance/connect/success";
export const insuranceFlexpaNotificationsUrl = (): string =>
  "/insurance/connect/insurance-notifications";
export const insuranceFlexpaAlternateInsuranceUrl = (): string =>
  "/insurance/connect/alternate-insurance";

// Insurance MMFA
export type InsuranceMmfaEnableUrlParams = {
  relinkMmfaId?: string;
};
export const insuranceMmfaEnableUrl = (
  params: InsuranceMmfaEnableUrlParams,
): string => {
  return buildUrlWithParams("/insurance/link/enable-mmfa", params);
};
export const insuranceMmfaPhoneNumberUrl = (): string =>
  "/insurance/link/mmfa/phone-number";
export const insuranceMmfaTermsConditionsUrl = (): string =>
  "/insurance/link/mmfa/terms-conditions";
export const insuranceMmfaSubmitUrl = (): string =>
  "/insurance/link/mmfa/submit";
export const insuranceMmfaErrorUrl = (): string => "/insurance/link/mmfa/error";

// Insurance MMFA Disable
export const insuranceMmfaDisableConfirmUrl = (linkId: string): string =>
  `/insurance/link/disable-mmfa/confirm?linkId=${linkId}`;
export const insuranceMmfaDisableConfirmReplacementUrl = (): string =>
  "/insurance/link/disable-mmfa/confirm-replacement";
export const insuranceMmfaDisableReplacementNumberUrl = (): string =>
  "/insurance/link/disable-mmfa/replacement-number";
export const insuranceMmfaDisableSubmitUrl = (): string =>
  "/insurance/link/disable-mmfa/submit";
export const insuranceMmfaDisableErrorUrl = (): string =>
  "/insurance/link/disable-mmfa/error";

// Note: feedback used to be its own route, but this now goes directly to the
// case screen with feedback params. The old route still exists, and redirects
// to the case screen, but this function goes directly to the case screen to
// save future deeplinks the bounce.
export const feedbackDeepLinkUrl = (params: FeedbackDeepLinkParams): string => {
  const caseUrl = issueUrl({ issueId: params.issueId, unread: true });
  return buildUrlWithParams(caseUrl, {
    showFeedback: params.showFeedback ?? "true",
    rating: params.rating?.toString(),
    feedbackId: params.feedbackId,
  });
};

// Person
export const peopleUrl = (): string => "/people";
export const peopleCreateUrl = (): string => "/people/create";
export const peopleEditUrl = (personId: string): string =>
  `/people/edit?id=${personId}`;

// Payment
export const paymentPreauthorizeUrl = (
  eventId?: string,
  issueId?: string,
): string => buildUrlWithParams("/payment/preauthorize", { eventId, issueId });
export const paymentSuccessUrl = (): string => "/payment/success";
export const paymentAgreementUrl = (
  eventId: string,
  issueId?: string,
): string =>
  buildUrlWithParams("/payment/payment-agreement", { eventId, issueId });

// Admin
export const shadowLoginUrl = (): string => "/shadow-login";

// Mobile only
export const previewUrl = (params: PreviewParams): string => {
  return `/preview?${qs.stringify(params)}`;
};

// Admin URLs
export const adminCaseUrl = (caseId: string): string =>
  `/apps/8a3eab1a-a5e0-11ee-b17a-5b761e2a2d1f/Case/Manage%20Case?id=${caseId}`;

export const adminUserUrl = (userId: string): string =>
  `/apps/a294ec12-b0d3-11ee-ba13-07bdb4642acc/User/Manage%20User?=&id=${userId}`;
